import { sortBy } from "lodash";
import React, { useEffect, useMemo } from "react";
import QRCode from "react-qr-code";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgDialog, IgSelect, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceDto,
  OrganizationDto,
  VariationDto,
} from "@intelligems/ig-types";
import { Grid, MenuItem } from "@mui/material";

import { getDialog, isDialogOpen, setDialog } from "../../store/slices/global";
import { ExperienceFe } from "../../types/schemas/experiences";
import { VariationFe } from "../../types/schemas/variations";
import { ClipboardCopy } from "../../utils/ClipboardCopy";
import { generatePreviewLink } from "../../utils/generatePreviewLink";

interface PreviewVariationCodeProps {
  organization: OrganizationDto;
  variations: Array<VariationDto | VariationFe>;
  experience: ExperienceFe | ExperienceDto;
}

export const PreviewVariationCode = ({
  experience,
  organization,
  variations,
}: PreviewVariationCodeProps) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(isDialogOpen("previewTestGroupCode"));

  const dialog = useSelector(getDialog("previewTestGroupCode"));
  const props = dialog.props as {
    id: string;
    path?: string;
    themeId?: string;
  };

  const handleClose = () => {
    dispatch(setDialog({ dialog: "previewTestGroupCode", open: false }));
  };

  const [selectedTestVariation, setSelectedTestVariation] = useImmer<
    VariationFe | VariationDto
  >(variations[0]);

  useEffect(() => {
    setSelectedTestVariation(variations[0]);
  }, [experience.id]);

  const codeUrl = useMemo(() => {
    return generatePreviewLink({
      organization,
      experience,
      variationId: selectedTestVariation?.id,
      previewThemeId: props?.themeId,
    });
  }, [organization, experience, selectedTestVariation, props?.themeId]);

  if (!variations?.length) {
    return null;
  }

  return (
    <IgDialog
      title={`Preview ${selectedTestVariation?.name}`}
      handleClose={handleClose}
      showDialog={isOpen}
      hideActions={true}
      showCloseIcon={true}
      dialogHeight={"fit-contents"}
      subtitle={
        <Grid
          item
          container
          justifyContent={"center"}
          marginBottom={"20px"}
          rowSpacing={2}
        >
          <Grid item>
            <QRCode
              size={256}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              value={codeUrl}
              viewBox={`0 0 256 256`}
            />
          </Grid>
          <IgTypography sx={{ marginY: "10px", fontSize: "14px" }}>
            This will open a preview of the '{selectedTestVariation?.name}' test
            group. To preview a different test group, use the dropdown below.
          </IgTypography>
          <IgSelect
            size={"small"}
            value={selectedTestVariation?.id || variations[0].id}
            onChange={(e) =>
              setSelectedTestVariation(
                variations.find(
                  (variation) => variation.id === e.target.value
                ) || variations[0]
              )
            }
          >
            {sortBy(variations, "order").map((variation, index) => (
              <MenuItem value={variation.id} key={index}>
                {variation?.name}
              </MenuItem>
            ))}
          </IgSelect>
          <ClipboardCopy
            codeFont={false}
            copyTextSx={{ fontSize: "14px", fontWeight: 600 }}
            copyText={codeUrl}
            showUrl={false}
            direction={"column"}
            copyButtonText={{ copy: "Copy Mobile URL", copied: "Copied!" }}
          />
        </Grid>
      }
    ></IgDialog>
  );
};
